import React, { useState, useEffect, useRef, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import PhotoSizeSelectSmallIcon from '@mui/icons-material/PhotoSizeSelectSmall';
import { makeStyles } from '@mui/styles';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import { useTranslation } from "react-i18next";
import {
  Modal,
  Grid,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
} from '@mui/material';
import Button from "components/CustomButtons/Button.js";
import CancelIcon from '@mui/icons-material/Cancel';
import AlertDialog from '../components/AlertDialog';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import {colors} from '../components/Theme/WebTheme';
import { isParcelRequired, isOptionsRequired } from 'common/sharedFunctions';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  submit3:{
    width:'100%',
    borderRadius:12,
    marginTop:2,
    padding:4
  },
  paper: {
    width: 560,
    maxWidth: '95vw',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    padding: theme.spacing(3),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  pageTitle: {
    fontWeight: 700,
    color: colors.DARK,
    fontSize: '1.5rem',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 12,
    backgroundColor: colors.PRIMARY,
    color: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#1D4ED8',
    },
  },
  tableContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.BORDER_BACKGROUND}`,
  },
}));

export default function CarTypes() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    editCarType
  } = api;
  const [data, setData] = useState([]);
  const cartypes = useSelector(state => state.cartypes);
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const classes = useStyles();
  const [open,setOpen] = useState(false);
  const [rowIndex,setRowIndex] = useState();
  const [modalType,setModalType] = useState();
  
  const onClick = (rowData) => {
    setImageData(rowData.image);
    setProfileModal(true);
    setUserData(rowData);
  };

  // Custom 5-column definition
  const columns = [
    {
      title: t('vehicle_type') || 'Vehicle Type',
      field: 'name',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: '#F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => onClick(rowData)}
          >
            {rowData.image ? (
              <img src={rowData.image} alt={rowData.name} style={{ width: 40, height: 40, objectFit: 'contain' }} />
            ) : (
              <DirectionsCarIcon sx={{ fontSize: 24, color: '#6B7280' }} />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.name || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.extra_info?.substring(0, 30) || t('no_description') || 'No description'}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '12px 16px' },
      editable: 'never',
    },
    {
      title: t('base_fare'),
      field: 'base_fare',
      render: rowData => (
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
          {settings?.symbol || ''}{rowData.base_fare || '0'}
        </Typography>
      ),
      cellStyle: { padding: '12px 16px' },
      editable: 'never',
    },
    {
      title: t('rate_per_unit_distance') || 'Rate/km',
      field: 'rate_per_unit_distance',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SpeedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {settings?.symbol || ''}{rowData.rate_per_unit_distance || '0'}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '12px 16px' },
      editable: 'never',
    },
    {
      title: t('min_fare'),
      field: 'min_fare',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {settings?.symbol || ''}{rowData.min_fare || '0'}
        </Typography>
      ),
      cellStyle: { padding: '12px 16px' },
      editable: 'never',
    },
    {
      title: t('position') || 'Position',
      field: 'pos',
      render: rowData => (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: '#EEF2FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6366F1' }}>
            {rowData.pos || '-'}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '12px 16px' },
      editable: 'never',
    },
  ];

  const subcolumns = [
    { title: t('description'), field: 'description', render: rowData => <span>{rowData.description}</span> },
    { title: t('amount'), field: 'amount', type: 'numeric' }
  ];

  const subcolumns2 = [
    { title: t('minsDelayed'), field: 'minsDelayed', render: rowData => <span>{rowData.minsDelayed}</span> },
    { title: t('amount'), field: 'amount', type: 'numeric' }
  ];

  const handleClose = () => {
    setOpen(false);
  }

  useEffect(() => {
    if (cartypes.cars) {
      setData(cartypes.cars);
    } else {
      setData([]);
    }
  }, [cartypes.cars]);

  const [selectedImage, setSelectedImage] = useState(null); 
  const handleProfileModal = (e) => {
    setProfileModal(false);
    setSelectedImage(null);
  }

  const [userData, setUserData] = useState();
  const [profileModal, setProfileModal] =  useState(false);
  const [imageData, setImageData] =  useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const [carFormOpen, setCarFormOpen] = useState(false);
  const [carFormMode, setCarFormMode] = useState('add');
  const [carFormData, setCarFormData] = useState({
    name: '',
    base_fare: 0,
    rate_per_unit_distance: 0,
    rate_per_hour: 0,
    min_fare: 0,
    convenience_fees: 0,
    convenience_fee_type: 'flat',
    pos: 1,
    extra_info: ''
  });

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

  const handleSetProfileModal = (e) =>{
    e.preventDefault();
    if(selectedImage){
      setLoading(true);
      let finalData = userData;
      finalData.image = selectedImage;
      dispatch(editCarType(finalData, "UpdateImage"));
      setProfileModal(false); 
      setTimeout(()=>{
        setSelectedImage(null);
        setLoading(false); 
      },10000);
    }
    else{
      setCommonAlert({ open: true, msg: t('choose_image_first') })
    }
  }

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtered data based on search
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(d =>
      (d.name && d.name.toLowerCase().includes(query))
    );
  }, [data, searchQuery]);

  const openCarForm = (mode, row) => {
    setCarFormMode(mode);
    if (mode === 'edit' && row) {
      setCarFormData({
        ...row
      });
    } else {
      setCarFormData({
        name: '',
        base_fare: 0,
        rate_per_unit_distance: 0,
        rate_per_hour: 0,
        min_fare: 0,
        convenience_fees: 0,
        convenience_fee_type: 'flat',
        pos: (data?.length || 0) + 1,
        extra_info: ''
      });
    }
    setCarFormOpen(true);
  };

  const handleSaveCar = () => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    if (!carFormData.name || !carFormData.name.trim()) {
      setCommonAlert({ open: true, msg: t('proper_input_name') || 'Please enter a name' });
      return;
    }
    const toNumber = (v) => isNaN(parseFloat(v)) ? 0 : parseFloat(v);
    const payload = {
      ...carFormData,
      base_fare: toNumber(carFormData.base_fare),
      rate_per_unit_distance: toNumber(carFormData.rate_per_unit_distance),
      rate_per_hour: toNumber(carFormData.rate_per_hour),
      min_fare: toNumber(carFormData.min_fare),
      convenience_fees: toNumber(carFormData.convenience_fees),
      pos: toNumber(carFormData.pos)
    };
    const method = carFormMode === 'edit' ? 'Update' : 'Add';
    dispatch(editCarType(payload, method));
    setCarFormOpen(false);
  };

  return (
    cartypes.loading ? <CircularLoading /> :
    <div ref={rootRef}>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        {/* Search Input */}
        <TextField
          size="small"
          placeholder={t('search') || 'Search vehicle types...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Divider */}
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />

        {/* All Pill */}
        <Chip
          label={t('all') || 'All'}
          sx={{
            bgcolor: '#111827',
            color: '#FFFFFF',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
          }}
        />

        {/* Results Count - Right aligned */}
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('car_type') || 'vehicle types'}
        </Typography>
        {settings?.AllowCriticalEditsAdmin ? (
          <Box
            onClick={() => openCarForm('add')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: colors.PRIMARY,
              color: '#fff',
              cursor: 'pointer',
              ml: 'auto',
              '&:hover': { bgcolor: '#1D4ED8' }
            }}
          >
            <AddIcon fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t('add')}</Typography>
          </Box>
        ) : null}
      </Box>

      <MaterialTable
        icons={TableIcons}
        title={null}
        columns={columns}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
        data={filteredData}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        detailPanel={rowData => (
          <Box sx={{ p: 3, bgcolor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
            <Grid container spacing={3}>
              {/* Fare Details */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AttachMoneyIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
                    {t('pricing') || 'Pricing'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('base_fare')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{settings?.symbol}{rowData.base_fare || '0'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('rate_per_unit_distance')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827' }}>{settings?.symbol}{rowData.rate_per_unit_distance || '0'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('rate_per_hour')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827' }}>{settings?.symbol}{rowData.rate_per_hour || '0'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('min_fare')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827' }}>{settings?.symbol}{rowData.min_fare || '0'}</Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Convenience Fee */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SpeedIcon sx={{ fontSize: 18, color: '#6366F1' }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
                    {t('convenience_fee') || 'Convenience Fee'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('amount')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{settings?.symbol}{rowData.convenience_fees || '0'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('type')}</Typography>
                    <Chip
                      label={rowData.convenience_fee_type === 'percentage' ? t('percentage') : t('flat')}
                      size="small"
                      sx={{
                        bgcolor: rowData.convenience_fee_type === 'percentage' ? '#EEF2FF' : '#F0FDF4',
                        color: rowData.convenience_fee_type === 'percentage' ? '#6366F1' : '#16A34A',
                        fontWeight: 500,
                        fontSize: 12,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('position')}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#111827' }}>{rowData.pos || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Extra Info */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DirectionsCarIcon sx={{ fontSize: 18, color: '#2563EB' }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>
                    {t('extra_info') || 'Extra Info'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                  {rowData.extra_info || t('no_description') || 'No additional information'}
                </Typography>
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', pt: 2, borderTop: '1px solid #E5E7EB' }}>
                  {isParcelRequired && (
                    <Tooltip title={t('parcel_types')}>
                      <Button
                        size="sm"
                        onClick={() => {
                          setModalType('parcelTypes');
                          setRowIndex(rowData.tableData.id);
                          setOpen(true);
                        }}
                        style={{
                          backgroundColor: '#EEF2FF',
                          color: '#6366F1',
                          borderRadius: 8,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        <PhotoSizeSelectSmallIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        {t('parcel_types')}
                      </Button>
                    </Tooltip>
                  )}
                  {isOptionsRequired && (
                    <Tooltip title={t('options')}>
                      <Button
                        size="sm"
                        onClick={() => {
                          setModalType('options');
                          setRowIndex(rowData.tableData.id);
                          setOpen(true);
                        }}
                        style={{
                          backgroundColor: '#F0FDF4',
                          color: '#16A34A',
                          borderRadius: 8,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        <FitnessCenterIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        {t('options')}
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title={t('cancelSlab')}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setModalType('cancelSlab');
                        setRowIndex(rowData.tableData.id);
                        setOpen(true);
                      }}
                      style={{
                        backgroundColor: '#FEF3C7',
                        color: '#D97706',
                        borderRadius: 8,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      <CancelScheduleSendIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {t('cancelSlab')}
                    </Button>
                  </Tooltip>
                  <Tooltip title={t('edit')}>
                    <IconButton
                      size="small"
                      onClick={() => openCarForm('edit', rowData)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: '#F3F4F6',
                        '&:hover': { bgcolor: '#E5E7EB' },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16, color: '#374151' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('delete')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (settings.AllowCriticalEditsAdmin) {
                          if (window.confirm(t('delete_message'))) {
                            dispatch(editCarType(rowData, "Delete"));
                          }
                        } else {
                          alert(t('demo_mode'));
                        }
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: '#FEE2E2',
                        '&:hover': { bgcolor: '#FECACA' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        options={{
          ...tableOptions,
          search: false,
          toolbar: false,
          showTitle: false,
          exportButton: true,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
          }),
        }}
        localization={{body:{
          addTooltip: (t('add')),
          deleteTooltip: (t('delete')),
          editTooltip: (t('edit')),
          emptyDataSourceMessage: (
            (t('blank_message'))
        ),
        editRow: {
          deleteText: (t('delete_message')),
          cancelTooltip: (t('cancel')),
          saveTooltip: (t('save'))
           },
          },
          toolbar: {
            searchPlaceholder: (t('search')),
            exportTitle: (t('export')),
          },
          header: {
            actions: (t('actions'))
        },
        pagination: {
          labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
         // labelRowsSelect: (t('rows')),
          firstTooltip: (t('first_page_tooltip')),
          previousTooltip: (t('previous_page_tooltip')),
          nextTooltip: (t('next_page_tooltip')),
          lastTooltip: (t('last_page_tooltip'))
        },
        }}
      />
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={profileModal}
        onClose={handleProfileModal}
        className={classes.modal}
        container={() => rootRef.current}
      >
      <Grid container spacing={1} className={classes.paper} style={{direction:isRTL==='rtl'?'rtl':'ltr'}}>
        <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography component="h1" variant="h6">
                {t('car_image')}

              <input
                  type="file"
                  style={{marginLeft:10}}
                  name= {t('image')}
                  onChange={(event) => {
                      setSelectedImage(event.target.files[0]);
                  }}
              />
            </Typography>
         </Grid>
         <Grid item xs={12} sm={12} md={12} lg={12}>
                  {selectedImage  && !loading ? 
                    <Tooltip title={t('cancel')}>
                      <CancelIcon onClick={()=>setSelectedImage(null)} style={{ fontSize: 30,backgroundColor:'#1a1a1a',borderRadius:50,color:'#ffffff' }}  />
                    </Tooltip>
                  : null }
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  {selectedImage ? 
                   <img alt="not fount" width={"200px"} height={"200px"}  src={URL.createObjectURL(selectedImage)} style={{marginTop:15,marginBottom:20}}/>
                   :
                    <img alt="not fount" width={"200px"} height={"200px"}  src={imageData} style={{marginTop:10}}/>
                  }
                <br />
                </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
          {loading ? 
            <Grid
              container
              spacing={0}
              alignItems="center"
              justify="center"
              style={{ minHeight: '5vh' }} >
              <CircularProgress/>
            </Grid>
            :
            <Grid item xs={12} sm={12} md={12} lg={12} style={{textAlign:isRTL==='rtl'?'right':'left'}}>
            <Button onClick={handleProfileModal} variant="contained" color="danger">
              {t('cancel')}
            </Button>
            <Button onClick={handleSetProfileModal} variant="contained" color="secondaryButton" style={{marginLeft:10}}>
              {t('save')}
            </Button>
            </Grid>
          }
          </Grid>
        </Grid>
      </Modal>
      {/* Add/Edit Car Type */}
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={carFormOpen}
        onClose={() => setCarFormOpen(false)}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid container spacing={2} className={classes.paper} style={{direction:isRTL==='rtl'?'rtl':'ltr'}}>
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700}>
              {carFormMode === 'edit' ? t('edit') || 'Edit vehicle' : t('add') || 'Add vehicle'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t('vehicle_type') || 'Vehicle type'} value={carFormData.name || ''} onChange={(e)=> setCarFormData({...carFormData, name:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('base_fare')} type="number" value={carFormData.base_fare} onChange={(e)=> setCarFormData({...carFormData, base_fare:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('rate_per_unit_distance')} type="number" value={carFormData.rate_per_unit_distance} onChange={(e)=> setCarFormData({...carFormData, rate_per_unit_distance:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('rate_per_hour')} type="number" value={carFormData.rate_per_hour} onChange={(e)=> setCarFormData({...carFormData, rate_per_hour:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('min_fare')} type="number" value={carFormData.min_fare} onChange={(e)=> setCarFormData({...carFormData, min_fare:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('convenience_fee') || 'Convenience fee'} type="number" value={carFormData.convenience_fees} onChange={(e)=> setCarFormData({...carFormData, convenience_fees:e.target.value})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('type')} value={carFormData.convenience_fee_type || 'flat'} onChange={(e)=> setCarFormData({...carFormData, convenience_fee_type:e.target.value || 'flat'})}/>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label={t('position') || 'Position'} type="number" value={carFormData.pos} onChange={(e)=> setCarFormData({...carFormData, pos:e.target.value})}/>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t('extra_info') || 'Description'} multiline minRows={2} value={carFormData.extra_info || ''} onChange={(e)=> setCarFormData({...carFormData, extra_info:e.target.value})}/>
          </Grid>
          <Grid item xs={12} sx={{ display:'flex', justifyContent:'flex-end', gap:1 }}>
            <Button onClick={()=> setCarFormOpen(false)} variant="contained" color="danger">{t('cancel')}</Button>
            <Button onClick={handleSaveCar} variant="contained" color="secondaryButton">{t('save')}</Button>
          </Grid>
        </Grid>
      </Modal>
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        onClose={handleClose}
        open={open}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <div className={classes.paper}>
          <MaterialTable
            icons={TableIcons}
            title={modalType === 'options'?t('options') :( modalType === 'cancelSlab' ? t('cancelSlab'): t('parcel_types'))}
            columns={modalType === 'cancelSlab'? subcolumns2 :subcolumns }
            data={(data[rowIndex] && data[rowIndex][modalType])?data[rowIndex][modalType]:[]}
            options={{
              ...tableOptions,
              exportButton: true,
            }}
            editable={{
              onRowAdd: newData =>
                settings.AllowCriticalEditsAdmin?
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    let tblData = data;
                    if(!tblData[rowIndex][modalType]){
                      tblData[rowIndex][modalType] = [];
                    }
                    tblData[rowIndex][modalType].push(newData);
                    dispatch(editCarType(tblData[rowIndex]), "Update");
                  }, 600);
                })
                :
                new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    alert(t('demo_mode'));
                  }, 600);
                }),
              onRowUpdate: (newData, oldData) =>
                settings.AllowCriticalEditsAdmin?
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    if(newData !== oldData){
                      let tblData = data;
                      
                      tblData[rowIndex][modalType][tblData[rowIndex][modalType].indexOf(oldData)] = newData;
                      delete tblData[rowIndex][modalType][tblData[rowIndex][modalType].indexOf(newData)].tableData;
                      dispatch(editCarType(tblData[rowIndex]), "Update");
                    }
                  }, 600);
                })
                :
                new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    alert(t('demo_mode'));
                  }, 600);
                }),
              onRowDelete: (oldData) =>
                settings.AllowCriticalEditsAdmin?
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    let tblData = data;
                    const index = tblData[rowIndex][modalType];
                    const neTtblData = index.filter((item) => 
                      item.amount !== oldData.amount && 
                      (modalType === 'cancelSlab' ? item.minsDelayed !== oldData.minsDelayed : item.description !== oldData.description));
                    tblData[rowIndex][modalType] = neTtblData;
                    dispatch(editCarType(tblData[rowIndex]), "Update");
                  }, 600);
                })
                :
                new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    alert(t('demo_mode'));
                  }, 600);
                }),
            }}  
          />
        </div>
      </Modal>
    </div>
  );
}
